import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { OrbitWordmark } from "@/components/Brand";
import { AuthBrandPanel } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", business_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome to ORBIT", { description: "Let's set up your business profile." });
      localStorage.setItem("orbit_goto", "/app/profile");
      navigate("/app/profile", { replace: true });
    } else setError(res.error);
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <AuthBrandPanel>
        <p className="relative mt-6 max-w-sm text-lg font-medium leading-relaxed">
          Create your workspace and discover where technology can create the most value in your business.
        </p>
      </AuthBrandPanel>

      <div className="flex flex-col justify-center px-6 py-12 sm:px-12">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-8 lg:hidden"><OrbitWordmark /></div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Start your assessment</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">A few details, then an 8–12 minute guided assessment.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
            <div className="space-y-1.5">
              <Label htmlFor="name">Your name</Label>
              <Input id="name" required value={form.name} onChange={set("name")} placeholder="Rajesh Sharma" data-testid="register-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="business">Business name</Label>
              <Input id="business" value={form.business_name} onChange={set("business_name")} placeholder="Your company" data-testid="register-business" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Work email</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="register-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={8} value={form.password} onChange={set("password")} placeholder="At least 8 characters" data-testid="register-password" />
            </div>
            {error && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="register-error">{error}</p>}
            <Button type="submit" className="h-11 w-full bg-accent text-accent-foreground hover:bg-accent/90" disabled={loading} data-testid="register-submit">
              {loading ? "Creating workspace…" : <>Create workspace <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-accent hover:underline" data-testid="to-login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
